// DeskDraftsPage — Brokerage Desk -> Assistant Drafts. One-click drafts built from the
// records (no AI), screened by the wording check, and put in the outbox to wait for a
// person: draft -> approved (sent through the outbox) / sent by me / discarded, with who
// approved. Nothing is sent from this page without a person pressing Approve.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

const NEEDS = {
    first_response_email: ['client_id', 'property_id?'], first_response_sms: ['client_id', 'property_id?'],
    followup: ['client_id', 'day', 'channel'], showing_confirm: ['showing_id', 'channel'], showing_reschedule: ['showing_id', 'new_time?', 'channel'],
    listing_description: ['property_id'], open_house: ['property_id', 'when'], offer_summary: ['offer_text', 'property_id?'],
    seller_report: ['client_id', 'property_id'], rental_status: ['application_id'], repair_ack: ['work_order_id', 'channel', 'to?'],
    vendor_dispatch: ['work_order_id'], daily_digest: ['agent_id?'], license_renewal: ['agent_id'],
};
const LABEL = { client_id: 'Client ID', property_id: 'Property ID', showing_id: 'Showing ID', application_id: 'Rental application ID', work_order_id: 'Work order #',
    agent_id: 'Licensee (agent) ID', day: 'Follow-up day', channel: 'Send by', new_time: 'Suggested new time', when: 'Date and time', offer_text: 'Paste the offer', to: 'Send to (if not on the lease)' };
const STATUS = { awaiting_approval: 'draft', queued: 'approved', sending: 'approved', sent: 'sent', delivered: 'sent', rejected: 'discarded', blocked: 'blocked', failed: 'failed', dead_letter: 'failed', scheduled: 'held' };

export default function DeskDraftsPage() {
    const api = useApiClient();
    const [kinds, setKinds] = useState({});
    // Other screens link here with ?kind=…&client_id=… (e.g. Licence tracking -> renewal reminder).
    const qs = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const [kind, setKind] = useState(qs.get('kind') || 'first_response_email');
    const [input, setInput] = useState(() => {
        const init = { channel: 'email', day: 1 };
        for (const k of ['client_id', 'property_id', 'showing_id', 'application_id', 'work_order_id', 'agent_id']) if (qs.get(k)) init[k] = qs.get(k);
        return init;
    });
    const [preview, setPreview] = useState(null);
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(null);
    const [msg, setMsg] = useState(null);

    const load = useCallback(async () => {
        try { setList(await api.get('/api/desk/drafts')); } catch (e) { setMsg({ bad: true, text: e.message }); }
    }, [api]);
    useEffect(() => { api.get('/api/desk/drafts/kinds').then((r) => setKinds(r.kinds)).catch(() => {}); load(); }, [api, load]);

    const body = () => ({ kind, ...input });
    const doPreview = async () => {
        setMsg(null);
        try { const r = await api.post('/api/desk/drafts/preview', body()); setPreview({ ...r.draft, ok: r.ok, findings: r.findings }); } catch (e) { setMsg({ bad: true, text: e.message }); }
    };
    const queue = async () => {
        try {
            const r = await api.post('/api/desk/drafts', { ...body(), subject: preview && preview.subject, body: preview && preview.body });
            setMsg({ text: `Draft #${r.message_id} is in the outbox waiting for approval.` }); setPreview(null); load();
        } catch (e) { setMsg({ bad: true, text: e.payload && e.payload.findings ? `${e.payload.note} ${e.payload.findings.map((f) => `"${f.term}"`).join(', ')}` : e.message }); }
    };
    const act = async (d, act, extra = {}) => {
        try { await api.post(`/api/desk/drafts/${d.message_id}/${act}`, extra); setOpen(null); load(); setMsg({ text: `Draft #${d.message_id}: ${act === 'approve' ? 'approved and handed to the outbox' : act === 'mark-sent' ? 'recorded as sent by you' : 'discarded'}.` }); }
        catch (e) { setMsg({ bad: true, text: e.message }); }
    };
    const copy = (text) => { try { navigator.clipboard.writeText(text); setMsg({ text: 'Copied.' }); } catch { /* clipboard blocked */ } };

    const field = (spec) => {
        const k = spec.replace('?', ''); const optional = spec.endsWith('?');
        const set = (v) => setInput({ ...input, [k]: v });
        if (k === 'channel') return <select value={input.channel || 'email'} onChange={(e) => set(e.target.value)} className="block p-2 border rounded"><option value="email">e-mail</option><option value="sms">text</option></select>;
        if (k === 'day') return <select value={input.day || 1} onChange={(e) => set(Number(e.target.value))} className="block p-2 border rounded">{[1, 3, 7, 14].map((d) => <option key={d} value={d}>day {d}</option>)}</select>;
        if (k === 'offer_text') return <textarea rows={6} value={input.offer_text || ''} onChange={(e) => set(e.target.value)} className="block w-full p-2 border rounded" />;
        return <input value={input[k] || ''} onChange={(e) => set(e.target.value)} placeholder={optional ? 'optional' : ''} className="block p-2 border rounded" />;
    };

    const columns = [
        { key: 'message_id', label: '#' },
        { key: 'created_at', label: 'Created', type: 'date' },
        { key: 'kind', label: 'Type', render: (r) => (kinds[r.kind] && kinds[r.kind].label) || r.kind },
        { key: 'to_address', label: 'To' },
        { key: 'client_name', label: 'Client' },
        { key: 'status', label: 'Status', value: (r) => STATUS[r.status] || r.status },
        { key: 'created_by', label: 'Drafted by' },
        { key: 'approved_by', label: 'Approved / discarded by' },
    ];
    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Assistant Drafts</h1>
                <p className="text-sm text-slate-600">Drafts are built from the records and checked for fair-housing and licence-law wording. Nothing is sent until a person approves it in the outbox (or sends it from their own e-mail or phone and records it here).</p>
                {msg && <p className={`mt-2 text-sm ${msg.bad ? 'text-red-700' : 'text-emerald-700'}`}>{msg.text}</p>}
            </div>
            <section className="bg-white p-6 rounded-lg shadow-md space-y-3">
                <h2 className="text-lg font-semibold">New draft</h2>
                <label className="text-sm block">Type<select value={kind} onChange={(e) => { setKind(e.target.value); setPreview(null); }} className="block p-2 border rounded">
                    {Object.entries(kinds).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select></label>
                <div className="flex flex-wrap gap-3">
                    {(NEEDS[kind] || []).map((spec) => <label key={spec} className={`text-sm ${spec.startsWith('offer_text') ? 'w-full' : ''}`}>{LABEL[spec.replace('?', '')]}{field(spec)}</label>)}
                </div>
                <button onClick={doPreview} className="px-3 py-2 text-sm bg-slate-200 rounded">Build draft</button>
                {preview && (
                    <div className="border rounded p-4 space-y-2 bg-slate-50">
                        <p className="text-sm text-slate-600">To: {preview.to} ({preview.channel === 'sms' ? 'text — the outbox adds the brand name and "Reply STOP to opt out."' : 'e-mail'})</p>
                        {preview.channel === 'email' && <input value={preview.subject || ''} onChange={(e) => setPreview({ ...preview, subject: e.target.value })} className="w-full p-2 border rounded text-sm" />}
                        <textarea rows={10} value={preview.body} onChange={(e) => setPreview({ ...preview, body: e.target.value })} className="w-full p-2 border rounded text-sm font-mono" />
                        {preview.findings && preview.findings.length > 0 && (
                            <ul className="text-sm">{preview.findings.map((f, i) => <li key={i} className={f.severity === 'block' ? 'text-red-700' : 'text-amber-700'}>{f.severity}: "{f.term}" — {f.why} {f.suggestion}</li>)}</ul>
                        )}
                        <div className="flex gap-2">
                            <button onClick={queue} className="px-3 py-2 text-sm bg-slate-800 text-white rounded">Put in the outbox for approval</button>
                            <button onClick={() => copy(preview.body)} className="px-3 py-2 text-sm bg-slate-200 rounded">Copy</button>
                        </div>
                    </div>
                )}
            </section>
            <section className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-lg font-semibold mb-2">Drafts</h2>
                <DataList rows={list} columns={columns} rowKey="message_id" csvName="assistant-drafts" defaultSort={{ key: 'message_id', dir: 'desc' }}
                    filters={[{ key: 'status', label: 'Status' }, { key: 'kind', label: 'Type' }]} onRowClick={setOpen} />
            </section>
            {open && (
                <section className="bg-white p-6 rounded-lg shadow-md space-y-2">
                    <h2 className="text-lg font-semibold">Draft #{open.message_id} — {STATUS[open.status] || open.status}</h2>
                    <p className="text-sm text-slate-600">To: {open.to_address}{open.subject ? ` · ${open.subject}` : ''}</p>
                    <pre className="whitespace-pre-wrap text-sm bg-slate-50 p-3 rounded">{open.body}</pre>
                    {open.error_text && <p className="text-sm text-slate-500">{open.error_text}</p>}
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => copy(open.body)} className="px-3 py-2 text-sm bg-slate-200 rounded">Copy</button>
                        {open.status === 'awaiting_approval' && <>
                            <button onClick={() => act(open, 'approve')} className="px-3 py-2 text-sm bg-slate-800 text-white rounded">Approve (send through the outbox)</button>
                            <button onClick={() => act(open, 'mark-sent')} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded">I sent it myself</button>
                            <button onClick={() => act(open, 'reject')} className="px-3 py-2 text-sm bg-slate-200 rounded">Discard</button>
                        </>}
                        <button onClick={() => setOpen(null)} className="px-3 py-2 text-sm">Close</button>
                    </div>
                </section>
            )}
        </div>
    );
}
